// src/pages/settings/sms/components/TestSmsPanel.jsx
import React, { useState } from 'react';
import { Send, RefreshCw, CheckCircle, XCircle, Phone } from 'lucide-react';
import { sendTestSms } from '../../../../services/api/smsConfig';
import { useAlert } from '../../../../contexts/AlertContext';
import styles from './TestSmsPanel.module.css';

const TestSmsPanel = ({ config }) => {
  const { success, error: showError } = useAlert();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('This is a test SMS from Rettzy. Your SMS configuration is working correctly.');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null); // { success: bool, message: string }
  const [phoneError, setPhoneError] = useState('');

  const smsLength = config?.gateway?.smsLength || 160;
  const gatewayName = config?.gateway?.name || 'Unknown';
  const isOverLimit = message.length > smsLength;

  const validatePhone = (value) => {
    if (!value.trim()) return 'Phone number is required';
    // Accept +94XXXXXXXXX or 07XXXXXXXX (Sri Lankan formats) or any +XX format
    const cleaned = value.replace(/\s+/g, '');
    if (!/^\+?\d{7,15}$/.test(cleaned)) return 'Enter a valid phone number';
    return '';
  };

  const handleSend = async () => {
    const phoneErr = validatePhone(phoneNumber);
    if (phoneErr) { setPhoneError(phoneErr); return; }
    if (isOverLimit) return;

    setPhoneError('');
    setResult(null);

    try {
      setIsSending(true);
      await sendTestSms({ phoneNumber: phoneNumber.trim(), message });
      setResult({ success: true, message: 'Test SMS queued successfully. Check your phone shortly.' });
      success('Test SMS queued');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send test SMS';
      setResult({ success: false, message: msg });
      showError(msg);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Send Test SMS</h3>
          <span className={styles.gatewayBadge}>{gatewayName}</span>
        </div>
        <p className={styles.sectionHint}>
          Send a test message to verify your SMS gateway is configured correctly.
          The SMS will be dispatched through the same queue used for real notifications.
        </p>
      </div>

      <div className={styles.section}>
        {/* Phone number */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <Phone size={14} />
            Recipient Phone Number
          </label>
          <input
            type="tel"
            className={`${styles.input} ${phoneError ? styles.inputError : ''}`}
            value={phoneNumber}
            onChange={e => { setPhoneNumber(e.target.value); setPhoneError(''); setResult(null); }}
            placeholder="+94771234567"
          />
          {phoneError && <span className={styles.errorText}>{phoneError}</span>}
          <small className={styles.hint}>Enter number in international format e.g. +94771234567</small>
        </div>

        {/* Message */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Test Message</label>
          <textarea
            className={`${styles.textarea} ${isOverLimit ? styles.inputError : ''}`}
            value={message}
            onChange={e => { setMessage(e.target.value); setResult(null); }}
            rows={4}
            placeholder="Enter your test message..."
          />
          <div className={styles.charBar}>
            <span className={isOverLimit ? styles.charOver : styles.charOk}>
              {message.length} / {smsLength} characters
            </span>
            {isOverLimit && <span className={styles.errorText}>Exceeds gateway limit</span>}
          </div>
        </div>

        {/* Result banner */}
        {result && (
          <div className={`${styles.resultBanner} ${result.success ? styles.resultSuccess : styles.resultError}`}>
            {result.success
              ? <CheckCircle size={18} />
              : <XCircle size={18} />}
            <span>{result.message}</span>
          </div>
        )}

        {/* Send button */}
        <div className={styles.actions}>
          <button
            className={styles.primaryBtn}
            onClick={handleSend}
            disabled={isSending || isOverLimit || !message.trim()}
          >
            {isSending
              ? <><RefreshCw size={16} className={styles.spin} /> Sending...</>
              : <><Send size={16} /> Send Test SMS</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestSmsPanel;